import _ from "lodash";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { Action, Premises } from "../constants/interfaces";
import api from "../utils/api";

interface State {
  premises: Premises[];
  error: Error | null;
  loading: boolean;
}

export function getInitialState(): State {
  return {
    premises: [],
    loading: false,
    error: null
  };
}

export const premisesTypes = {
  GET_PREMISES_REQUEST: "GET_PREMISES_REQUEST",
  GET_PREMISES_RESPONSE: "GET_PREMISES_RESPONSE",
  GET_PREMISE_REQUEST: "GET_PREMISE_REQUEST",
  GET_PREMISE_RESPONSE: "GET_PREMISE_RESPONSE",
  UPDATE_PREMISE_REQUEST: "UPDATE_PREMISE_REQUEST",
  UPDATE_PREMISE_RESPONSE: "UPDATE_PREMISE_RESPONSE"
};

export const premisesActions: any = {
  getPremises: () => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    dispatch({ type: premisesTypes.GET_PREMISES_REQUEST });
    try {
      const { data: premises } = await api.get("/contact/getUserPremises");
      dispatch({
        type: premisesTypes.GET_PREMISES_RESPONSE,
        payload: premises,
        error: false
      });
    } catch (e) {
      dispatch({
        type: premisesTypes.GET_PREMISES_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  },

  getPremise: (id: string) => async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>
  ) => {
    dispatch({ type: premisesTypes.GET_PREMISE_REQUEST });
    try {
      const { data: premise } = await api.get(
        `/contact/getUserPremisesDetails/${id}`
      );

      dispatch({
        type: premisesTypes.GET_PREMISE_RESPONSE,
        payload: premise,
        error: false
      });
      return premise;
    } catch (e) {
      dispatch({
        type: premisesTypes.GET_PREMISE_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  },

  updatePremise: (premise: Premises, callback?: () => void) => async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>
  ) => {
    dispatch({ type: premisesTypes.UPDATE_PREMISE_REQUEST });
    try {
      await api.put(
        `contact/updateUserPremisesDetails/${premise.cmAcmCaseIdentifier}`,
        premise
      );
      dispatch({
        type: premisesTypes.UPDATE_PREMISE_RESPONSE,
        payload: premise,
        error: false
      });
      if (callback) callback();
    } catch (e) {
      dispatch({
        type: premisesTypes.UPDATE_PREMISE_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  }
};

export default function premise(state = getInitialState(), action: Action) {
  const { type, payload, error } = action;

  switch (type) {
    case premisesTypes.GET_PREMISES_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case premisesTypes.GET_PREMISES_RESPONSE: {
      if (error) {
        return {
          ...state,
          loading: false,
          error: payload,
          premises: state.premises
        };
      }
      return { ...state, loading: false, error: null, premises: payload };
    }

    case premisesTypes.GET_PREMISE_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case premisesTypes.GET_PREMISE_RESPONSE: {
      if (error) {
        //  In the case of the error, we don't modify the reducer state
        return {
          ...state,
          loading: false,
          error: payload,
          premises: state.premises
        };
      }
      // In the case of success, we merge the existing entity record with retrieved record
      const newPremises: Premises = payload;
      const premises = mergePremiseWithState(newPremises, state.premises);
      return { ...state, loading: false, error: null, premises };
    }

    case premisesTypes.UPDATE_PREMISE_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case premisesTypes.UPDATE_PREMISE_RESPONSE: {
      if (error) {
        return { ...state, loading: false, error: payload };
      }
      // In the case of success, we merge the existing entity record with retrieved record
      const newPremises: Premises = payload;
      const premises = mergePremiseWithState(newPremises, state.premises);
      return { ...state, loading: false, error: null, premises };
    }
  }
  return state;
}

export const mergePremiseWithState = (
  updatedPremise: Premises,
  existingPremises: Premises[]
): Premises[] => {
  const premises = [...existingPremises];
  const existingEntityIndex: number = _.findIndex(
    premises,
    (premise: Premises) =>
      premise.cmAcmCaseIdentifier === updatedPremise.cmAcmCaseIdentifier
  );
  const existingPremise = premises[existingEntityIndex];
  existingEntityIndex === -1
    ? // If the entity doesn't exist in state, add to reducer
      premises.push(updatedPremise)
    : // If the entity already exists, merge new data
      (premises[existingEntityIndex] = _.merge(
        {},
        existingPremise,
        updatedPremise
      ));
  return premises;
};
